import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import InteractivePresentation from '../../components/InteractivePresentation';
import { Database, Sparkles, Search, BookOpen, RefreshCw, Play, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { catalogAPI, aiAPI } from '../../lib/api';
import { toast } from 'sonner';

const categories = ['Technology', 'Business', 'Leadership', 'Communication', 'Design', 'Marketing', 'Finance', 'Data Science', 'Human Resources', 'Personal Development'];
const difficulties = ['beginner', 'intermediate', 'advanced'];

export default function CatalogManagerPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedCount, setSeedCount] = useState(500);
  const [seedCategory, setSeedCategory] = useState('');
  
  const [generatingLesson, setGeneratingLesson] = useState(false);
  const [lessonTopic, setLessonTopic] = useState('');
  const [lessonDifficulty, setLessonDifficulty] = useState('intermediate');
  const [generatedLesson, setGeneratedLesson] = useState(null);
  const [generatedQuiz, setGeneratedQuiz] = useState(null);
  
  const [creatingCourse, setCreatingCourse] = useState(false);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const res = await catalogAPI.getStats();
      setStats(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await catalogAPI.seed(seedCount, seedCategory || null);
      toast.success(res.data.message);
      fetchStats();
    } catch (e) { toast.error('Seeding failed'); }
    finally { setSeeding(false); }
  };

  const handleGenerateLesson = async () => {
    if (!lessonTopic) { toast.error('Enter a topic'); return; }
    setGeneratingLesson(true);
    setGeneratedLesson(null);
    setGeneratedQuiz(null);
    try {
      const res = await aiAPI.generateLesson({ topic: lessonTopic, difficulty: lessonDifficulty, include_quiz: true });
      setGeneratedLesson(res.data.lesson);
      setGeneratedQuiz(res.data.quiz);
      toast.success('Lesson generated!');
    } catch (e) { toast.error('Generation failed'); console.error(e); }
    finally { setGeneratingLesson(false); }
  };

  const handleCreateCourse = async () => {
    if (!lessonTopic) { toast.error('Enter a topic'); return; }
    setCreatingCourse(true);
    try {
      const res = await aiAPI.createCourseFromLesson(lessonTopic, lessonDifficulty);
      toast.success('Course created! Go to Manage Courses to publish it.');
    } catch (e) { toast.error('Course creation failed'); }
    finally { setCreatingCourse(false); }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <h1 className="font-sketch text-4xl text-ink">Course Catalog Manager</h1>
          <p className="text-muted-foreground">Seed courses and generate AI content</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="stats-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center"><Database className="w-6 h-6 text-blue-600" /></div>
                <div><p className="text-sm text-muted-foreground">Total Courses</p><p className="font-sketch text-3xl">{stats.total_courses}</p></div>
              </div>
            </div>
            <div className="stats-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center"><BookOpen className="w-6 h-6 text-green-600" /></div>
                <div><p className="text-sm text-muted-foreground">Published</p><p className="font-sketch text-3xl">{stats.published_courses}</p></div>
              </div>
            </div>
            <div className="stats-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center"><Sparkles className="w-6 h-6 text-purple-600" /></div>
                <div><p className="text-sm text-muted-foreground">Categories</p><p className="font-sketch text-3xl">{Object.keys(stats.by_category || {}).length}</p></div>
              </div>
            </div>
            <div className="stats-card">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center"><Database className="w-6 h-6 text-yellow-600" /></div>
                <div><p className="text-sm text-muted-foreground">Providers</p><p className="font-sketch text-3xl">{Object.keys(stats.by_provider || {}).length}</p></div>
              </div>
            </div>
          </div>
        )}

        {/* Category breakdown */}
        {stats?.by_category && Object.keys(stats.by_category).length > 0 && (
          <div className="card-sketchy">
            <h3 className="font-sketch text-xl mb-4">Courses by Category</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_category).map(([cat, count]) => (
                <Badge key={cat} variant="outline" className="text-sm py-1 px-3">{cat}: {count}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Seed Courses */}
        <div className="card-sketchy">
          <div className="flex items-center gap-3 mb-4">
            <Database className="w-6 h-6 text-primary" />
            <h3 className="font-sketch text-xl">Seed Course Catalog</h3>
          </div>
          <p className="text-muted-foreground mb-4">Add thousands of curated courses from top educational topics</p>
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div>
              <Label>Number of Courses</Label>
              <Input type="number" value={seedCount} onChange={e => setSeedCount(parseInt(e.target.value))} className="input-sketchy" min={100} max={10000} step={100} />
            </div>
            <div>
              <Label>Category (optional)</Label>
              <Select value={seedCategory} onValueChange={setSeedCategory}>
                <SelectTrigger className="input-sketchy"><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button className="btn-sketchy w-full" onClick={handleSeed} disabled={seeding}>
                {seeding ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Seeding...</> : <><RefreshCw className="w-4 h-4 mr-2" />Seed Courses</>}
              </Button>
            </div>
          </div>
        </div>

        {/* AI Content Generator */}
        <div className="card-sketchy">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-primary" />
            <h3 className="font-sketch text-xl">AI Content Generator</h3>
          </div>
          <p className="text-muted-foreground mb-4">Generate interactive whiteboard-style lessons with AI</p>
          
          <div className="grid sm:grid-cols-3 gap-4 mb-4">
            <div className="sm:col-span-2">
              <Label>Topic</Label>
              <Input value={lessonTopic} onChange={e => setLessonTopic(e.target.value)} className="input-sketchy" placeholder="e.g., Introduction to Machine Learning" />
            </div>
            <div>
              <Label>Difficulty</Label>
              <Select value={lessonDifficulty} onValueChange={setLessonDifficulty}>
                <SelectTrigger className="input-sketchy"><SelectValue /></SelectTrigger>
                <SelectContent>{difficulties.map(d => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex gap-4">
            <Button className="btn-sketchy" onClick={handleGenerateLesson} disabled={generatingLesson || !lessonTopic}>
              {generatingLesson ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Play className="w-4 h-4 mr-2" />Generate Lesson</>}
            </Button>
            <Button variant="outline" className="border-2 border-black" onClick={handleCreateCourse} disabled={creatingCourse || !lessonTopic}>
              {creatingCourse ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <><BookOpen className="w-4 h-4 mr-2" />Create Full Course</>}
            </Button>
          </div>
        </div>

        {/* Generated Lesson Preview */}
        {generatedLesson && (
          <div className="card-sketchy p-0 overflow-hidden">
            <div className="bg-primary text-white p-4">
              <h3 className="font-sketch text-xl">Generated Lesson Preview</h3>
            </div>
            <div className="p-6">
              <InteractivePresentation lesson={generatedLesson} onComplete={() => toast.success('Lesson completed!')} />
            </div>
          </div>
        )}

        {/* Generated Quiz */}
        {generatedQuiz && (
          <div className="card-sketchy">
            <h3 className="font-sketch text-xl mb-4">Generated Quiz ({generatedQuiz.length} questions)</h3>
            <div className="space-y-4">
              {generatedQuiz.map((q, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg border-2 border-black">
                  <p className="font-bold mb-2">Q{idx + 1}: {q.question}</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {q.options.map((opt, i) => (
                      <div key={i} className={`p-2 rounded ${i === q.correct_answer ? 'bg-green-100 border-green-500' : 'bg-white'} border`}>
                        {String.fromCharCode(65 + i)}. {opt}
                      </div>
                    ))}
                  </div>
                  {q.explanation && <p className="text-sm text-muted-foreground mt-2 italic">{q.explanation}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
